import sfPointsCaclulator from "@/func/game/sfPointsCaclulator";
import apiCall from "@/func/net/apiCall";
import userHasRole from "@/func/user/userHasRole";
import { SfUser } from "@/types/generalTypes";
import { SfActivitySaveData } from "@/types/sfActivityTypes";
import {
  SfSpellingActivityInfo,
  SfSpellingTestAnswerWord,
  SfSpellingTestMode,
  SfWord,
} from "@/types/spellingTypes";
import { ref, computed } from "vue";

const spellingGameState = ref({
  activityInfo: {
    activityId: 0,
    gameId: 0,
    wordCategoryId: null,
    testId: null,
    wordListId: null,
    trickyWords: false,
  } as SfSpellingActivityInfo,
  saveData: null as SfActivitySaveData | null,
  saveStatus: 0,
  saveErrorMessage: '',
  testResultId: null as null | number,
  loadedWords: null as SfWord[] | null,
  mode: "test" as SfSpellingTestMode,
  code: "",
  user: null as SfUser | null,
  saveAttempts: 0,
  gameStatus: 'intro' as 'intro' | 'playing' | 'results',
});

export const spellingActivityInfo = computed(
  () => spellingGameState.value.activityInfo,
);
export const spellingActivitySaveStatus = computed(
  () => spellingGameState.value.saveStatus,
);
export const spellingActivitySaveData = computed(
  () => spellingGameState.value.saveData,
);
export const spellingActivityLoadedWords = computed(
  () => spellingGameState.value.loadedWords,
);
export const spellingActivityResultId = computed(
  () => spellingGameState.value.testResultId,
);
export const spellingActivitySaveAttempts = computed(
  () => spellingGameState.value.saveAttempts,
);
export const spellingGameStatus = computed(
  () => spellingGameState.value.gameStatus,
);
export const spellingSaveErrorMessage = computed(
  () => spellingGameState.value.saveErrorMessage
);

export const setSpellingGameSaveStatus = (status: number, message = '') => {
  spellingGameState.value.saveStatus = status;
  spellingGameState.value.saveErrorMessage = message;
}


export const initSpellingGameState = (
  user: SfUser | null,
  activityInfo: SfSpellingActivityInfo,
) => {
  spellingGameState.value.user = user;
  spellingGameState.value.activityInfo = activityInfo;
  spellingGameState.value.gameStatus = 'intro';
};

export const spellingStartGame = async (mode: SfSpellingTestMode) => {
  spellingGameState.value.mode = mode;
  setSpellingGameSaveStatus(0);
  const postData = { ...spellingActivityInfo.value, mode };
  const data = await apiCall(
    "activity-words/start-activity",
    null,
    { method: "POST" },
    postData
  );
  if (data.success) {
    spellingGameState.value.code = data.data.code;
    spellingGameState.value.loadedWords = data.data.words;
    spellingGameState.value.gameStatus = 'playing';
    const res = { words: data.data.words };
    return res;
  }
  return null;
};

export const spellingActivityIsPerfect = (
  words: SfSpellingTestAnswerWord[],
) => {
  return words.reduce((res, el) => res && el.attempts.length == 1, true);
};

export const spellingSaveGameData = (words: SfSpellingTestAnswerWord[]) => {
  spellingGameState.value.saveData = {
    ...spellingActivityInfo.value,
    mode: spellingGameState.value.mode,
    studentId: spellingGameState.value.user
      ? spellingGameState.value.user.id
      : null,
    code: spellingGameState.value.code,
    words,
    sfPoints: sfPointsCaclulator(
      words,
      spellingGameState.value.mode,
      spellingActivityIsPerfect(words),
      spellingActivityInfo.value.activityId,
    ),
  };
  spellingGameState.value.testResultId = null;
  spellingGameState.value.saveAttempts = 0;
  if (
    spellingGameState.value.user &&
    userHasRole(spellingGameState.value.user, ["student", "individual"])
  ) {
    spellingGameAttemptSave();
  } else {
    setSpellingGameSaveStatus(2);
    spellingGameState.value.gameStatus = 'results';
  }
  return true;
};

export const spellingGameAttemptSave = async () => {
  if (!spellingGameState.value.saveData) {
    return;
  }
  setSpellingGameSaveStatus(1);
  const url = "activity-words/save-result";
  const resData = await apiCall(
    url,
    null,
    { method: "POST" },
    spellingGameState.value.saveData,
    null,
    true
  );
  spellingGameState.value.saveAttempts =
    spellingGameState.value.saveAttempts + 1;
  if (resData.success) {
    setSpellingGameSaveStatus(2);
    spellingGameState.value.testResultId = resData.data.test_result_id;
    // how to handle when it runs out of tricky words
    // if(resData.data.is_tricky_words == 1) {
    //     if(resData.data.tricky_words_count < 5) {
    //         window.location = '/dashboard';
    //     }
    // }
    spellingGameState.value.gameStatus = 'results';
    return true;
  } else if(resData.httpStatus == 0) {
    setSpellingGameSaveStatus(-1);
  } else {
    setSpellingGameSaveStatus(-2, resData.errorDetails?.message);
  }
};

export const spellingGameCloseResults = () => {
  setSpellingGameSaveStatus(0);
  spellingGameState.value.saveStatus = 0;
  spellingGameState.value.saveData = null;
  spellingGameState.value.gameStatus = 'intro';
};
